import React, { useRef, useState, useEffect, useContext } from "react";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import PropTypes from "prop-types";

import Button from "components/CustomButtons/Button";
import Link from "@mui/material/Link";

import {
  setCookie,
  dropCookie,
  isDoNotTrackEnabled,
  cookiePreference,
  COOKIE_CONTROL_PREFERENCES
} from "core/cookie";
import { AppContext } from "app_context";

import cx from "classnames";
import makeStyles from "@mui/styles/makeStyles";
import styles from "assets/jss/default/components/cookieAndPrivacyStyle.js";
const useStyles = makeStyles(styles);

export default function CookieAndPrivacy(props) {
  const { showPrivacySettings } = props;
  const classes = useStyles();
  const showCustomise = useRef(false);
  const { pushInstruction } = useMatomo();
  const [customise, setCustomise] = useState(classes.gdprCookieTypes);
  const [showMessage, setShowMesssage] = useState(
    cx([classes.gdprCookieMessage, classes.removeMessage])
  );
  const [analyticsAccepted, setAnalyticsAccepted] = useState(false);
  const [preferencesAccepted, setPreferencesAccepted] = useState(false);

  const appContext = useContext(AppContext);

  useEffect(() => {
    setAnalyticsAccepted(Boolean(cookiePreference("analytics")));
    setPreferencesAccepted(Boolean(cookiePreference("preferences")));
  }, [showPrivacySettings]);

  useEffect(() => {
    if (showPrivacySettings) {
      setShowMesssage(classes.gdprCookieMessage);
    } else {
      setShowMesssage(cx([classes.gdprCookieMessage, classes.removeMessage]));
      setCustomise(classes.gdprCookieTypes);
      showCustomise.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrivacySettings]);

  const showCustomiseTag = () => {
    const customiseClass = !showCustomise.current
      ? classes.gdprCookieTypesL
      : classes.gdprCookieTypes;

    setCustomise(customiseClass);
    showCustomise.current = !showCustomise.current;
  };

  const setCookies = (usePreferences, useAnalytics) => {
    const cookiePrefs = {
      necessary: 1,
      preferences: Number(usePreferences),
      analytics: Number(useAnalytics)
    };

    dropCookie(true, 30);
    setCookie(COOKIE_CONTROL_PREFERENCES, JSON.stringify(cookiePrefs), 365);

    const analyticsTrackingAllowed = cookiePreference("analytics");
    const constentGiven = analyticsTrackingAllowed
      ? "setConsentGiven"
      : "forgetConsentGiven";

    // Mark that the current user has consented. The consent is one-time only,
    // so in a subsequent browser session, the user will have to consent again.
    pushInstruction(constentGiven);

    appContext.showPrivacySetting(false);
  };

  return (
    <div className={showMessage}>
      <h4>🍪 Cookies &amp; Privacy</h4>
      <p>
        By clicking “Accept All Cookies”, you agree to the storing of cookies on
        your device to enhance site navigation, analyze site usage, and assist
        in our marketing efforts.
        <br />
        {isDoNotTrackEnabled() ? (
          <>
            <br />
            You are not being tracked between our applications because of your
            browser&apos;s settings, but we would appreciate if you would
            whitelist this site as it would help us to improve our service.
          </>
        ) : (
          <br />
        )}
        <a target="blank" href="https://onyxinsight.com/privacy/">
          More information
        </a>
        <Link
          className={classes.customiseLink}
          underline="none"
          onClick={() => {
            showCustomiseTag();
          }}
        >
          Customise Cookies
        </Link>
      </p>
      <div id="gdpr-cookie-types" className={customise}>
        <p>
          Cookies enable you to personalize your experience on our sites, tell
          us which parts of our websites people have visited, and give us
          insight into user behavior so we can improve our products. You may
          choose to prevent this website from aggregating and analyzing the
          actions you take here. Doing so will protect your privacy, but will
          also prevent us from learning from your actions and creating a better
          experience for you and other users.
        </p>
        <h5>Select cookies to accept</h5>
        <ul>
          <li>
            <input
              type="checkbox"
              name="gdpr[]"
              value="necessary"
              checked="checked"
              disabled="disabled"
            />
            <label title="These are cookies that are essential for the website to work correctly.">
              Necessary
            </label>
          </li>
          <li>
            <input
              type="checkbox"
              name="gdpr[]"
              value="preferences"
              checked={preferencesAccepted}
              data-auto="off"
              onChange={event => setPreferencesAccepted(event.target.checked)}
            />
            <label title="These are cookies that are related to your site preferences, e.g. remembering your username, site colours, etc.">
              Site Preferences
            </label>
          </li>
          <li>
            <input
              type="checkbox"
              name="gdpr[]"
              value="analytics"
              checked={analyticsAccepted}
              data-auto="off"
              onChange={event => setAnalyticsAccepted(event.target.checked)}
            />
            <label title="Cookies related to site visits, browser types, etc.">
              Analytics
            </label>
          </li>
        </ul>
        <Button
          onClick={() => {
            setCookies(preferencesAccepted, analyticsAccepted);
          }}
        >
          Confirm My Choices
        </Button>
      </div>
      <p>
        <Button
          onClick={() => {
            setCookies(false, false);
          }}
        >
          Reject All Cookies
        </Button>
        <Button
          onClick={() => {
            setCookies(true, true);
          }}
        >
          Accept All Cookies
        </Button>
      </p>
    </div>
  );
}

CookieAndPrivacy.propTypes = {
  showPrivacySettings: PropTypes.bool.isRequired
};
